import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuickplayRematch } from '../api/quickplayApi';

const REMATCH_NOT_READY_RETRY_DELAYS_MS = [1500, 2500, 4000];

function isRematchNotReadyError(error) {
    return (
        error?.response?.status === 409 &&
        error?.response?.data?.code === 'QUICKPLAY_REMATCH_NOT_READY'
    );
}

function useQuickplayRematchNavigation() {
    const navigate = useNavigate();
    const quickplayRematch = useQuickplayRematch();
    const [error, setError] = useState('');
    const [isStartingRematch, setIsStartingRematch] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const startRematch = async () => {
        setError('');
        setIsStartingRematch(true);

        try {
            for (let attempt = 0; attempt <= REMATCH_NOT_READY_RETRY_DELAYS_MS.length; attempt += 1) {
                try {
                    const result = await quickplayRematch.mutateAsync();
                    if (!result?.leagueSlug || !result?.matchId) {
                        throw new Error('Nova created the rematch but did not return its destination.');
                    }
                    if (mountedRef.current) {
                        navigate(`/${result.leagueSlug}/match/${result.matchId}/match`);
                    }
                    return;
                } catch (rematchError) {
                    const shouldRetry = (
                        isRematchNotReadyError(rematchError) &&
                        attempt < REMATCH_NOT_READY_RETRY_DELAYS_MS.length
                    );

                    if (!shouldRetry) {
                        if (mountedRef.current) {
                            setError(
                                rematchError?.response?.data?.error ||
                                rematchError?.message ||
                                'Nova could not start the rematch. Please try again.'
                            );
                        }
                        return;
                    }

                    await new Promise((resolve) => {
                        window.setTimeout(resolve, REMATCH_NOT_READY_RETRY_DELAYS_MS[attempt]);
                    });
                    if (!mountedRef.current) {
                        return;
                    }
                }
            }
        } finally {
            if (mountedRef.current) {
                setIsStartingRematch(false);
            }
        }
    };

    return {
        error,
        isStartingRematch,
        startRematch
    };
}

export {
    REMATCH_NOT_READY_RETRY_DELAYS_MS,
    useQuickplayRematchNavigation
};
